/*
Credentials ride in query strings in a few places: GeoViz appends the Firebase ID token as
`apiToken` on tiler requests (a query param avoids the CORS preflight a custom header would
force), and the API client reads `apiKey` off the page URL. Anything that ships a URL off the
device — Sentry breadcrumbs, spans, transaction names — has to mask them first.
*/
const SENSITIVE_QUERY_PARAMS = [
  'apiToken',
  'apiKey',
  'access_token',
  'accessToken',
  'id_token',
  'idToken',
  'refresh_token',
  'token',
  'jwt',
  'password',
  'secret',
  'signature',
];

export const REDACTED = '[Filtered]';

// Matched against raw strings rather than parsed URLs so this also covers relative URLs, strings
// that merely embed one (span descriptions like `GET https://…`), and bare query strings with no
// leading `?` (the shape Sentry's extractQueryParamsFromUrl produces for request.query_string).
const SENSITIVE_PARAM_RE = new RegExp(
  `((?:^|[?&])(?:${SENSITIVE_QUERY_PARAMS.join('|')})=)[^&#\\s"']*`,
  'gi'
);

export function redactUrl<T>(value: T): T {
  if (typeof value !== 'string' || !value.includes('=')) {
    return value;
  }
  return value.replace(SENSITIVE_PARAM_RE, `$1${REDACTED}`) as unknown as T;
}

// Breadcrumb and span payloads nest URLs a level or two down (a console breadcrumb keeps the
// un-joined log arguments under `data.arguments`), so walk into arrays and plain objects. Class
// instances are left alone: rebuilding one as a plain object changes how Sentry serializes it.
const MAX_DEPTH = 5;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function redactValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') {
    return redactUrl(value);
  }

  if (depth >= MAX_DEPTH) {
    return value;
  }

  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((entry) => {
      const redacted = redactValue(entry, depth + 1);
      changed = changed || redacted !== entry;
      return redacted;
    });
    return changed ? next : value;
  }

  if (isPlainObject(value)) {
    return redactRecord(value, depth);
  }

  return value;
}

function redactRecord(data: Record<string, unknown>, depth: number): Record<string, unknown> {
  let changed = false;
  const redacted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const next = redactValue(value, depth + 1);
    changed = changed || next !== value;
    redacted[key] = next;
  }

  return changed ? redacted : data;
}

export function redactUrlsInRecord(
  data: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!data) {
    return data;
  }

  return redactRecord(data, 0);
}
